import * as React from 'react';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { useState, useEffect } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Grid, Typography, Paper, TextField } from "@mui/material";
import { useToken } from '../items/UseToken';
import CasinoIcon from "@mui/icons-material/Casino";

const steps = ['Enter a name', 'Connect with Command Center'];

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

export default function CloudMachineWizardStepper(props) {
    const [activeStep, setActiveStep] = useState(0);
    const [newMachineGuid, setNewMachineGuid] = useState(null);
    const [cloudName, setCloudName] = useState("");
    const [machineList, setMachineList] = useState([]);
    const [nameAvailable, setNameAvailable] = useState(null);
    const [registerCompleted, setRegisterCompleted] = useState(false);
    const { token } = useToken();

    // 🔄 Pobierz listę istniejących maszyn
    useEffect(() => {
        fetch("api/getmachines", {
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
            },
        })
            .then((res) => res.json())
            .then((res) => {
                setMachineList(res.results || []);
            })
            .catch((err) => console.error("Error fetching machines:", err));
    }, [token]);

    // 🔍 Sprawdzaj czy nazwa się nie powtarza
    useEffect(() => {
        if (!cloudName) return;
        const exists = machineList.some(
            (m) => m.name?.toLowerCase() === cloudName.toLowerCase()
        );
        setNameAvailable(!exists);
    }, [cloudName, machineList]);

    // ✨ Generator nazw (bez myślników, tylko litery/cyfry)
    const adjectives = [
        "cosmic",
        "ancient",
        "stellar",
        "mystic",
        "lunar",
        "desert",
        "iron",
        "obsidian",
        "amber",
        "sacred",
        "forgotten",
        "celestial",
    ];
    const nouns = [
        "dune",
        "obelisk",
        "orbit",
        "pyramid",
        "nebula",
        "sphinx",
        "relic",
        "nova",
        "artifact",
        "meteor",
        "temple",
        "horizon",
    ];

    const generateName = () => {
        let newName = "";
        do {
            const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
            const noun = nouns[Math.floor(Math.random() * nouns.length)];
            newName = `${adj}${noun}`; // 🔥 bez myślnika
        } while (
            machineList.some(
                (m) => m.name?.toLowerCase() === newName.toLowerCase()
            )
        );
        setCloudName(newName);
        setNameAvailable(true);
    };

    // ✅ Walidacja inputu (tylko litery i cyfry)
    const handleNameChange = (e) => {
        const value = e.target.value;
        // odrzuca wszystko poza a-z, A-Z, 0-9
        if (/^[a-zA-Z0-9]*$/.test(value)) {
            setCloudName(value);
        }
    };

    // 🔑 Tworzenie nowego workera po kliknięciu "Next"
    const generateNewCloudMachineKey = () => {
        const requestOptions = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
            },
            body: JSON.stringify({
                workerId: cloudName,
            }),
        };
        fetch("api/createcloudworker", requestOptions)
            .then((res) => res.text())
            .then((res) => {
                console.log(res);
                setNewMachineGuid(res);
            })
            .catch((err) => console.error(err));
    };

    const nameInputSection = (
        <Box textAlign="center">
            <Typography variant="h6" sx={{ mb: 3 }}>
                Enter a name for your new worker
            </Typography>
            <Box sx={{ display: "inline-flex", gap: 1 }}>
                <TextField
                    label="Name"
                    variant="outlined"
                    value={cloudName}
                    onChange={handleNameChange}
                    sx={{ mb: 1, width: "260px" }}
                    color={nameAvailable ? "success" : "error"}
                    helperText={
                        cloudName
                            ? !/^[a-zA-Z0-9]+$/.test(cloudName)
                                ? "❌ Only letters and numbers allowed"
                                : nameAvailable
                                    ? "✓ Available"
                                    : "✗ Already exists"
                            : " "
                    }
                />
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={generateName}
                    sx={{ height: "56px", minWidth: "56px" }}
                >
                    <CasinoIcon />
                </Button>
            </Box>
        </Box>
    );

    // 🔁 Polling
    useEffect(() => {
        if (!newMachineGuid) return;
        const interval = setInterval(() => {
            isUserConnected();
        }, 2000);
        return () => clearInterval(interval);
    }, [newMachineGuid]);

    function isUserConnected() {
        fetch("api/getmachineusers?machinekey=" + newMachineGuid, {
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
            },
        })
            .then((res) => res.json())
            .then((res) => {
                if (res.results.length > 0) setRegisterCompleted(true);
            })
            .catch((err) => console.log(err));
    }

    const handleNext = () => {
        if (!cloudName || !/^[a-zA-Z0-9]+$/.test(cloudName) || !nameAvailable) return;
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        if (activeStep === 0) generateNewCloudMachineKey();
    };

    const stepText2 = (
        <>
            <p><b>Worker deployment is in progress</b></p>
            <p>You can wait or close this screen</p>
        </>
    );
    const stepCompleted = "Machine successfully connected";

    return (
        <>
            <Box sx={{ width: '100%', zoom: '150%' }}>
                <Stepper size="large" activeStep={activeStep}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>

            <Box sx={{ flexGrow: 1, marginTop: '20vh', textAlign: 'center' }}>
                <Typography sx={{ textAlign: 'center' }} variant="h5">
                    {(activeStep === 1 && !registerCompleted) && stepText2}
                    {(activeStep === 1 && registerCompleted) && stepCompleted}
                </Typography>

                {activeStep === 0 && nameInputSection}

                {activeStep === 1 && (
                    <Grid container spacing={0}>
                        <Grid item xs />
                        <Grid sx={{ textAlign: 'center', padding: '5%' }} item xs={6}>
                            <Card sx={{ minWidth: 275 }}>
                                <CardContent>
                                    {!registerCompleted && <CircularProgress color="info" sx={{ fontSize: 100 }} />}
                                    {registerCompleted && <CheckCircleOutlineIcon color="success" sx={{ fontSize: 100 }} />}
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs />
                    </Grid>
                )}

                {activeStep < 1 && cloudName && /^[a-zA-Z0-9]+$/.test(cloudName) && nameAvailable && (
                    <Button onClick={handleNext} size="large" variant="contained">Next</Button>
                )}

                {registerCompleted && (
                    <Button onClick={props.handleDialogClose} size="large" variant="contained">Done</Button>
                )}
            </Box>
        </>
    );
}
